/** Atlas keys for the namespaces the full-screen panels draw.
 *
 * `theme/art.ts` already resolves portraits, secondary-skill icons, creature
 * frames and town buildings; this adds the four namespaces only these screens
 * need - spells, artifacts, town backgrounds and battlefields - and re-exports
 * nothing, so there is exactly one definition of each mapping in the package.
 *
 * Like its neighbour, this file is a stop-gap until `packages/render` ships the
 * general `assetKeyFor(def)` resolver (STATUS.md, "the second-biggest gap").
 */

import type { ArtifactDef, CreatureDef, SpellDef } from "@game/core";
import { buildingKey, creatureFrameKey, portraitKey } from "../theme/art.js";

const stripExt = (s: string): string => {
  const dot = s.lastIndexOf(".");
  return dot > 0 ? s.slice(0, dot) : s;
};

const leafOf = (path: string): string => {
  const parts = path.split("/");
  return stripExt(parts[parts.length - 1] ?? "");
};

/** `SPELLS/assets/<school>/<name>.png` -> `spell.<school>.<name>`. */
export function spellIconKey(art: string): string | null {
  const parts = art.split("/");
  const school = parts[parts.length - 2];
  const file = parts[parts.length - 1];
  if (school === undefined || file === undefined) return null;
  return `spell.${school}.${stripExt(file)}`;
}

/**
 * `ARTIFACTS/assets/<name>.png` -> `artifact.<name>`, and the five war machines,
 * which are authored as artifacts but packed under `warmachine.*`.
 *
 * Only 22 unique artifact icons exist for 157 artifacts; the data already
 * points each one at the icon it shares, so resolving off `art` rather than off
 * the id is what makes all 157 draw something.
 */
export function artifactIconKey(art: string): string | null {
  const leaf = leafOf(art);
  if (leaf === "") return null;
  if (art.startsWith("BATTLE/")) return `warmachine.${leaf.replace(/^wm_/, "")}`;
  return `artifact.${leaf}`;
}

/** `BATTLE/assets/battlefields/bf_<name>.png` -> `battlefield.<name>`. */
export function battlefieldKey(art: string): string | null {
  const leaf = leafOf(art);
  return leaf === "" ? null : `battlefield.${leaf.replace(/^bf_/, "")}`;
}

export const townBackgroundKey = (faction: string): string => `town.${faction}.background`;

// ------------------------------------------------- convenience, by def

export const spellIcon = (def: SpellDef): string | null => spellIconKey(def.art);

export const artifactIcon = (def: ArtifactDef): string | null => artifactIconKey(def.art);

/**
 * A creature's battle-sheet frame, with the neutral exception.
 *
 * Six neutrals have no battle sheet at all: their `art` points into the
 * map-object set, which is packed under `obj.*` and has exactly one frame.
 */
export function creatureIcon(def: CreatureDef, frame = 0): string | null {
  if (def.art.startsWith("TILES/")) return `obj.${leafOf(def.art)}`;
  // The four war machines are creature defs on the battlefield but their art is
  // packed with the artifacts that field them, one frame under `warmachine.*`.
  if (def.art.startsWith("BATTLE/")) return artifactIconKey(def.art);
  return creatureFrameKey(def.art, frame);
}

export const buildingIcon = (art: string): string | null => buildingKey(art);

export const heroPortrait = (art: string): string | null => portraitKey(art);
